import { useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';

interface NetworkContextType {
  isOffline: boolean;
}

const NetworkContext = createContext<NetworkContextType>({ isOffline: false });

export const useNetworkState = () => useContext(NetworkContext);

export const NetworkManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);

    const handleOnline = async () => {
      setIsOffline(false);

      try {
        // Resume paused mutations after confirming auth
        await refreshUser();
        await queryClient.resumePausedMutations();
      } catch (error) {
        console.error('Failed to restore session after coming online:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [queryClient, refreshUser]);

  return <NetworkContext.Provider value={{ isOffline }}>{children}</NetworkContext.Provider>;
};
